/**
 * FeaturedBadge Component
 * 
 * Displays visual badges for featured listings with variant-specific styling.
 * Supports multiple badge variants with icons, gradients, and customizable labels.
 */

'use client';

import { 
  Star, 
  Flame, 
  Sparkles, 
  Crown, 
  TrendingDown, 
  Eye, 
  Gem, 
  Leaf,
  BadgeDollarSign,
} from 'lucide-react';
import type { FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';

// =============================================================================
// TYPES
// =============================================================================

export interface FeaturedBadgeProps {
  /** The badge variant determining styling and icon */
  variant: FeaturedBadgeVariant;
  /** Optional custom label (overrides default label for variant) */
  label?: string | null;
  /** Badge size */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the icon */
  showIcon?: boolean;
}

interface BadgeConfig {
  icon: React.ElementType;
  bg: string;
  text: string;
  defaultLabel: string;
  border?: string;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const BADGE_CONFIG: Record<FeaturedBadgeVariant, BadgeConfig> = {
  FEATURED: { 
    icon: Star, 
    bg: 'bg-gradient-to-r from-amber-400 to-orange-500', 
    text: 'text-white',
    defaultLabel: 'Featured',
  },
  HOT: { 
    icon: Flame, 
    bg: 'bg-gradient-to-r from-red-500 to-orange-500', 
    text: 'text-white',
    defaultLabel: 'Hot Property',
  },
  NEW: { 
    icon: Sparkles, 
    bg: 'bg-gradient-to-r from-blue-500 to-cyan-500', 
    text: 'text-white',
    defaultLabel: 'Just Listed',
  },
  EXCLUSIVE: { 
    icon: Crown, 
    bg: 'bg-gradient-to-r from-purple-600 to-pink-500', 
    text: 'text-white',
    defaultLabel: 'Exclusive',
  },
  PREMIUM: { 
    icon: Crown, 
    bg: 'bg-gradient-to-r from-yellow-500 to-amber-600', 
    text: 'text-white',
    defaultLabel: 'Premium',
  },
  PRICE_REDUCED: { 
    icon: TrendingDown, 
    bg: 'bg-gradient-to-r from-green-500 to-emerald-600', 
    text: 'text-white',
    defaultLabel: 'Price Reduced',
  },
  MUST_SEE: { 
    icon: Eye, 
    bg: 'bg-gradient-to-r from-indigo-500 to-purple-500', 
    text: 'text-white',
    defaultLabel: 'Must See',
  },
  INVESTMENT: { 
    icon: BadgeDollarSign, 
    bg: 'bg-gradient-to-r from-emerald-600 to-teal-600', 
    text: 'text-white',
    defaultLabel: 'Investment',
  },
  LUXURY: { 
    icon: Gem, 
    bg: 'bg-gradient-to-r from-slate-800 to-slate-900', 
    text: 'text-white',
    defaultLabel: 'Luxury',
  },
  ECO_FRIENDLY: { 
    icon: Leaf, 
    bg: 'bg-gradient-to-r from-green-600 to-lime-500', 
    text: 'text-white',
    defaultLabel: 'Eco-Friendly',
  },
};

const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-sm gap-1.5',
  lg: 'px-3 py-1.5 text-base gap-2',
};

const ICON_SIZES = {
  sm: 10,
  md: 12,
  lg: 14,
};

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * FeaturedBadge - Displays a styled badge for featured content
 * 
 * @example
 * ```tsx
 * <FeaturedBadge variant="FEATURED" />
 * <FeaturedBadge variant="HOT" label="Hot Deal" size="lg" />
 * <FeaturedBadge variant="LUXURY" showIcon={false} />
 * ```
 */
export function FeaturedBadge({ 
  variant, 
  label, 
  size = 'md',
  className = '',
  showIcon = true,
}: FeaturedBadgeProps) {
  const config = BADGE_CONFIG[variant];
  
  if (!config) {
    // Fallback for unknown variants
    return null;
  }
  
  const Icon = config.icon;
  const displayLabel = label || config.defaultLabel;
  
  return (
    <span 
      className={`
        inline-flex items-center font-semibold rounded-full shadow-md
        ${config.bg} ${config.text} ${SIZE_CLASSES[size]}
        ${config.border || ''}
        ${className}
      `.trim()}
    >
      {showIcon && (
        <Icon 
          size={ICON_SIZES[size]} 
          className="flex-shrink-0" 
          strokeWidth={2.5}
        />
      )}
      <span className="whitespace-nowrap">{displayLabel}</span>
    </span>
  );
}

// =============================================================================
// VARIANT COMPONENTS (for convenience)
// =============================================================================

/** Featured badge */
export function FeaturedBadgeFeatured(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="FEATURED" {...props} />;
}

/** Hot property badge */
export function FeaturedBadgeHot(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="HOT" {...props} />;
}

/** New listing badge */
export function FeaturedBadgeNew(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="NEW" {...props} />;
}

/** Exclusive badge */
export function FeaturedBadgeExclusive(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="EXCLUSIVE" {...props} />;
}

/** Premium badge */
export function FeaturedBadgePremium(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="PREMIUM" {...props} />;
}

/** Price reduced badge */
export function FeaturedBadgePriceReduced(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="PRICE_REDUCED" {...props} />;
}

/** Luxury badge */
export function FeaturedBadgeLuxury(props: Omit<FeaturedBadgeProps, 'variant'>) {
  return <FeaturedBadge variant="LUXURY" {...props} />;
}

export default FeaturedBadge;
